/**
 * Reload Loop Breaker Plugin
 *
 * Detects and breaks infinite reload loops caused by service worker updates,
 * particularly on iOS Safari.
 *
 * **Server-side**: Injects inline script into <head> that tracks reloads via
 * sessionStorage. If >2 reloads in 2 minutes, performs nuclear reset (clear
 * all caches, unregister all SWs, reload) and stashes diagnostics.
 *
 * **Client-side**: Reports stashed diagnostics to Sentry after the clean reload.
 *
 * Pattern follows `plugins/early-error-capture.ts`.
 */

declare global {
  interface Window {
    __reloadLoopDetected?: boolean
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  // SERVER-SIDE: Inject reload loop detection script into <head>
  if (process.server) {
    injectBreakerScript(nuxtApp)
    return
  }

  // CLIENT-SIDE: Report stashed diagnostics to Sentry
  reportReloadLoop(nuxtApp)
})

/**
 * Server-side: Inject inline <head> script for reload loop detection.
 * Must run before any other JS to catch loops that happen before hydration.
 */
function injectBreakerScript(nuxtApp: any) {
  const alfredVersion = useRuntimeConfig().public.alfredVersion || 'unknown'

  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- Nuxt app hooks are untyped
  nuxtApp.hooks.hook('app:rendered', () => {
    useHead({
      script: [
        {
          id: 'reload-loop-breaker',
          tagPriority: -10, // Must run before any other JS
          innerHTML: `
(function() {
  try {
    var KEY = '__reload_ts';
    var VH_KEY = '__alfred_version_history';
    var VH_MAX = 10;
    var RL_HISTORY_KEY = '__reload_loop_incidents';
    var RL_HISTORY_MAX = 10;
    var WINDOW_MS = 120000;
    var THRESHOLD = 2;
    var now = Date.now();
    var VERSION = ${JSON.stringify(alfredVersion)};

    // Test mode: ?testReloadLoop=true seeds fake timestamps to trigger breaker
    var testMode = window.location.search.indexOf('testReloadLoop=true') !== -1;

    // --- Detect reload source ---
    // Known sources: pwa-update, loop-breaker, chunk-error, reload-util, map-error, payment-retry, clear-data
    // 'untagged' means no source was set — could be manual F5, browser recovery, or untagged code
    var reloadSource = 'untagged';
    try {
      var src = sessionStorage.getItem('__reload_source');
      if (src) { reloadSource = src; sessionStorage.removeItem('__reload_source'); }
    } catch (e) {}

    // --- Reload history: each entry is {t, v, s} (timestamp, version, source) ---
    var raw = sessionStorage.getItem(KEY);
    var entries = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(entries)) entries = [];

    // Migrate legacy format (bare timestamps) to {t, v, s} entries
    if (entries.length > 0 && typeof entries[0] === 'number') {
      entries = entries.map(function(ts) { return {t: ts, v: 'unknown', s: 'unknown'}; });
    }

    // In test mode, inject fake recent entries to exceed the threshold
    if (testMode) {
      entries = [{t: now - 1000, v: VERSION, s: 'test'}, {t: now - 2000, v: VERSION, s: 'test'}];
      console.warn('[Reload Loop] Test mode — injecting fake entries to trigger breaker');
    }

    // Filter to recent entries within the 2-minute window
    var recent = [];
    for (var i = 0; i < entries.length; i++) {
      if (now - entries[i].t < WINDOW_MS) recent.push(entries[i]);
    }

    // --- Version history (localStorage): append on build version change ---
    try {
      var vhRaw = localStorage.getItem(VH_KEY);
      var vh = vhRaw ? JSON.parse(vhRaw) : [];
      if (!Array.isArray(vh)) vh = [];
      var lastVh = vh.length > 0 ? vh[vh.length - 1] : null;
      if (!lastVh || lastVh.build !== VERSION) {
        vh.push({build: VERSION, runtime: null, t: new Date().toISOString()});
        while (vh.length > VH_MAX) vh.shift();
        localStorage.setItem(VH_KEY, JSON.stringify(vh));
      }
    } catch (e) { /* localStorage unavailable */ }

    if (recent.length >= THRESHOLD) {
      // Reload loop detected — nuclear reset
      window.__reloadLoopDetected = true;
      console.warn('[Reload Loop] Detected ' + (recent.length + 1) + ' reloads in 2 min — clearing caches + SWs');

      // --- Record incident in localStorage for repeat-offender tracking ---
      var priorIncidents = 0;
      try {
        var rlRaw = localStorage.getItem(RL_HISTORY_KEY);
        var rlHistory = rlRaw ? JSON.parse(rlRaw) : [];
        if (!Array.isArray(rlHistory)) rlHistory = [];
        priorIncidents = rlHistory.length;
        rlHistory.push({t: new Date().toISOString(), v: VERSION, url: window.location.pathname, count: recent.length + 1});
        while (rlHistory.length > RL_HISTORY_MAX) rlHistory.shift();
        localStorage.setItem(RL_HISTORY_KEY, JSON.stringify(rlHistory));
      } catch (e) { /* localStorage unavailable */ }

      // Stash diagnostic context for post-hydration Sentry report
      try {
        var reportReloads = recent.concat({t: now, v: VERSION, s: reloadSource});
        sessionStorage.setItem('__reload_loop_report', JSON.stringify({
          count: recent.length + 1,
          reloads: reportReloads,
          priorIncidents: priorIncidents,
          url: window.location.href,
          userAgent: navigator.userAgent,
          referrer: document.referrer
        }));
      } catch (e) { /* storage full — diagnostics lost, reset still happens */ }

      // Clear the reload counter
      sessionStorage.removeItem(KEY);

      // Nuclear reset: delete all caches + unregister all SWs, then reload clean
      var cacheP = window.caches
        ? caches.keys().then(function(keys) {
            return Promise.all(keys.map(function(key) { return caches.delete(key); }));
          })
        : Promise.resolve();

      var swP = navigator.serviceWorker
        ? navigator.serviceWorker.getRegistrations().then(function(regs) {
            return Promise.all(regs.map(function(reg) { return reg.unregister(); }));
          })
        : Promise.resolve();

      Promise.all([cacheP, swP]).catch(function() {}).then(function() {
        // Tag this reload so next page load knows it came from the breaker
        try { sessionStorage.setItem('__reload_source', 'loop-breaker'); } catch(e) {}
        if (testMode) {
          var url = new URL(window.location.href);
          url.searchParams.delete('testReloadLoop');
          window.location.replace(url.toString());
        } else {
          window.location.reload();
        }
      });
      return;
    }

    // No loop — record this reload entry and continue normally
    recent.push({t: now, v: VERSION, s: reloadSource});
    sessionStorage.setItem(KEY, JSON.stringify(recent));
    console.warn('[Reload Loop] Reload ' + recent.length + '/' + THRESHOLD + ' in window (source: ' + reloadSource + ', no action)');
  } catch (e) {
    // sessionStorage unavailable (old Safari private browsing, storage full, etc.)
    // Silently degrade — reload tracking simply doesn't work, which is acceptable.
  }
})();
          `.trim(),
        },
      ],
    })
  })
}

/**
 * Client-side: Read stashed reload loop report from sessionStorage and
 * send it to Sentry. Runs once after the clean reload that follows a
 * nuclear reset.
 */
interface ReloadEntry {
  t: number
  v: string
  s?: string // source: 'pwa-update' | 'loop-breaker' | 'chunk-error' | 'reload-util' | 'map-error' | 'payment-retry' | 'clear-data' | 'untagged'
}

interface ReloadLoopReport {
  count: number
  reloads?: ReloadEntry[]
  priorIncidents?: number
  timestamps?: number[] // Legacy format — remove after v4.47 (sessionStorage, expires with session)
  url: string
  userAgent: string
  referrer: string
}

interface ReloadLoopIncident {
  t: string
  v: string
  url: string
  count: number
}

function reportReloadLoop(nuxtApp: any) {
  if (typeof window === 'undefined') return

  let report: ReloadLoopReport | null = null

  try {
    const raw = sessionStorage.getItem('__reload_loop_report')
    if (!raw) return
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- sessionStorage JSON, validated by null check below
    report = JSON.parse(raw)
    sessionStorage.removeItem('__reload_loop_report')
  } catch {
    return
  }

  if (!report) return

  // Format reload entries — support both new {t,v,s} and legacy [timestamp] formats
  const formatReload = (r: ReloadEntry) =>
    `${r.v} [${r.s || 'unknown'}] @ ${new Date(r.t).toISOString()}`
  const reloads = report.reloads
    ? report.reloads.map(formatReload)
    : (report.timestamps?.map((t) => new Date(t).toISOString()) ?? [])

  // Read full incident history from localStorage for repeat-offender context
  let incidentHistory: ReloadLoopIncident[] = []
  try {
    const histRaw = localStorage.getItem('__reload_loop_incidents')
    if (histRaw) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- our own localStorage JSON
      incidentHistory = JSON.parse(histRaw)
    }
  } catch {
    /* localStorage unavailable */
  }

  const isRepeat = (report.priorIncidents ?? 0) > 0

  console.error(
    `[Reload Loop] Detected ${report.count} reloads in 2 minutes — nuclear reset performed` +
      (isRepeat
        ? ` (repeat offender: ${report.priorIncidents} prior incidents)`
        : ''),
    { count: report.count, reloads, url: report.url },
  )

  // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- TODO: Type nuxtApp properly
  const sentry = nuxtApp.$sentry

  if (sentry) {
    const error = new Error(
      isRepeat
        ? `Reload loop detected (repeat): ${report.count} reloads in 2 minutes`
        : `Reload loop detected: ${report.count} reloads in 2 minutes`,
    )
    error.name = isRepeat ? 'ReloadLoopDetectedRepeat' : 'ReloadLoopDetected'

    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access -- TODO: Type sentry properly
    sentry.captureException(error, {
      level: 'error',
      tags: {
        'reload-loop': true,
        'reload-loop-repeat': isRepeat,
        'error-source': 'reload-loop-breaker',
      },
      extra: {
        reloadCount: report.count,
        reloads,
        priorIncidents: report.priorIncidents ?? 0,
        incidentHistory: incidentHistory.map(
          (i) => `${i.v} x${i.count} ${i.url} @ ${i.t}`,
        ),
        url: report.url,
        userAgent: report.userAgent,
        referrer: report.referrer,
      },
    })
  }
}
